import React, { useState, useEffect, useRef } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import './QRScanner.css';

const ClientQRScanner = ({ onScanSuccess, onScanError, onHide }) => {
  const [scanning, setScanning] = useState(true);
  const [error, setError] = useState('');
  const [hasPermission, setHasPermission] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [scanLog, setScanLog] = useState([]);
  const scannerRef = useRef(null);
  const lastScanTime = useRef(0);

  // Vérifier les permissions caméra
  useEffect(() => {
    const checkCameraPermission = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        });

        stream.getTracks().forEach(track => track.stop());
        setHasPermission(true);
      } catch (err) {
        console.error('Erreur permission caméra:', err);
        setHasPermission(false);
        setError(err.name === 'NotAllowedError'
          ? 'Permission caméra refusée'
          : 'Aucune caméra trouvée');
      }
    };

    checkCameraPermission();
  }, []);

  // Logs de debug
  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `[${timestamp}] ${message}`;
    setScanLog(prev => [...prev.slice(-9), logEntry]); // Garder 10 dernières entrées
    console.log(`[${type.toUpperCase()}] ${message}`);
  };

  // Initialiser le scanner avec APPROCHE ALTERNATIVE
  useEffect(() => {
    if (!hasPermission || !scanning) return;

    addLog('Initialisation du scanner...', 'start');

    // 1. Nettoyer tout scanner existant
    const cleanup = () => {
      if (scannerRef.current) {
        try {
          scannerRef.current.clear().catch(e => {
            addLog(`Erreur cleanup: ${e.message}`, 'warning');
          });
          scannerRef.current = null;
        } catch (e) {
          console.log('Cleanup déjà fait');
        }
      }

      // Nettoyer le conteneur
      const container = document.getElementById('scanner-container');
      if (container) {
        container.innerHTML = '';
      }
    };

    cleanup();

    // 2. Attendre un peu avant de créer le nouveau scanner
    setTimeout(() => {
      try {
        addLog('Création du nouveau scanner...', 'process');

        // CONFIGURATION ULTRA SIMPLIFIÉE
        const config = {
          fps: 2,  // TRÈS BAS pour fiabilité
          qrbox: 350,  // Nombre simple, pas d'objet
          aspectRatio: 1.0,
          disableFlip: false,
          rememberLastUsedCamera: true
        };

        // Créer le scanner
        scannerRef.current = new Html5QrcodeScanner(
          "scanner-container",
          config,
          false
        );

        // FONCTION DE SUCCÈS avec logs détaillés
        const onScanSuccess = (decodedText, decodedResult) => {
          const now = Date.now();
          if (now - lastScanTime.current < 2000) {
            addLog('Scan ignoré (anti-rebond)', 'warning');
            return;
          }

          lastScanTime.current = now;
          addLog(`SCAN RÉUSSI! Texte: ${decodedText.substring(0, 50)}...`, 'success');

          // Afficher une alerte visuelle
          alert(`🎉 QR CODE DÉTECTÉ!\n\n${decodedText.substring(0, 100)}`);

          // Arrêter le scanner
          scannerRef.current?.pause();
          setIsProcessing(true);

          // Parser et envoyer les données
          processQRData(decodedText);
        };

        // FONCTION D'ERREUR
        const onScanFailure = (error) => {
          // Log seulement les erreurs importantes
          if (!error.includes('No QR code') && !error.includes('NotFoundException')) {
            addLog(`Erreur scan: ${error}`, 'error');
          }
        };

        // Démarrer le scanner
        scannerRef.current.render(onScanSuccess, onScanFailure);
        addLog('Scanner démarré avec succès', 'success');

        // Vérifier l'état après démarrage
        setTimeout(() => {
          const video = document.querySelector('#scanner-container video');
          if (video && video.videoWidth > 0) {
            addLog(`Caméra active: ${video.videoWidth}x${video.videoHeight}`, 'success');
          } else {
            addLog('Avertissement: Caméra non détectée', 'warning');
          }
        }, 1000);

      } catch (error) {
        addLog(`ERREUR initialisation: ${error.message}`, 'error');
        setError(`Erreur scanner: ${error.message}`);
      }
    }, 500); // Délai pour éviter les conflits

    // Cleanup
    return cleanup;

  }, [hasPermission, scanning]);

  // Traiter les données QR
  const processQRData = async (decodedText) => {
    try {
      addLog(`Traitement du QR code: ${decodedText.substring(0, 30)}...`, 'process');

      let qrData;

      // Parser le JSON du QR code
      if (decodedText.trim().startsWith('{')) {
        try {
          const data = JSON.parse(decodedText);
          console.log('📊 QR Code JSON parsé:', data);

          // FORMAT UNIFIÉ pour correspondre au backend
          qrData = {
            // Le code QR lui-même (le plus important)
            code: data.c || data.code || data.qrCode || decodedText,

            // Les points (peuvent être dans différents champs)
            points: parseInt(data.p || data.points || data.giftPointsTotal || 0),

            // La facture
            billNumber: data.b || data.billNumber || data.facture || '',

            // Localisation
            location: data.l || data.location || data.localisation || 'SPINTOSS',

            // Type de QR code
            type: data.type || 'SPINTOSS_POINTS',

            // Données brutes
            rawData: decodedText,

            // Items (si présents)
            items: data.items || [],

            // Autres infos
            totalAmount: data.total || data.totalAmount || 0,
            date: data.date || new Date().toISOString(),
            scanSource: 'camera'
          };

        } catch (jsonError) {
          console.log('⚠️ QR Code n\'est pas JSON, traitement comme texte');
          qrData = {
            type: 'TEXT_CODE',
            code: decodedText,
            rawData: decodedText,
            date: new Date().toISOString(),
            scanSource: 'camera'
          };
        }
      } else {
        // QR code texte simple
        console.log('📝 QR Code texte simple:', decodedText);

        qrData = {
          type: 'TEXT_CODE',
          code: decodedText,
          rawData: decodedText,
          date: new Date().toISOString(),
          scanSource: 'camera'
        };

        // Détecter automatiquement les codes KdoXXX
        if (decodedText.toLowerCase().includes('kdo')) {
          qrData.type = 'SPINTOSS_POINTS';

          // Extraire les points du format KdoXXX
          const match = decodedText.match(/kdo(\d+)/i);
          if (match) {
            qrData.points = parseInt(match[1]);
            console.log('✅ Points extraits KdoXXX:', qrData.points);
          }
        }
      }

      // Appeler le callback parent avec les données bien formatées
      console.log('📤 Données envoyées au parent:', qrData);
      if (onScanSuccess) {
        onScanSuccess(qrData);
      }

      addLog('Données envoyées au parent', 'success');

      // Fermer après délai
      setTimeout(() => {
        if (onHide) onHide();
      }, 1500);

    } catch (error) {
      addLog(`Erreur traitement: ${error.message}`, 'error');
      if (onScanError) onScanError(error);
      setIsProcessing(false);
      setScanning(true);
    }
  };

  // Tester avec un scan manuel
  const testManualScan = () => {
    const testData = {
      type: "SPINTOSS_POINTS",
      points: 5,
      billNumber: "TEST",
      code: "SPINTOSS-TEST-123",
      location: "spintoss-FaB",
      items: [],
      totalAmount: 0,
      date: new Date().toISOString(),
      rawData: { type: "SPINTOSS_POINTS", points: 5 },
      scanSource: 'test'
    };

    addLog('Test manuel déclenché', 'test');

    if (onScanSuccess) {
      onScanSuccess(testData);
    }

    setTimeout(() => {
      if (onHide) onHide();
    }, 1000);
  };

  // Vérifier l'état du scanner
  const checkScannerStatus = () => {
    addLog('=== VÉRIFICATION SCANNER ===', 'debug');

    const video = document.querySelector('#scanner-container video');
    const canvas = document.querySelector('#scanner-container canvas');

    if (video) {
      addLog(`✓ Vidéo: ${video.videoWidth}x${video.videoHeight}`, 'success');
    } else {
      addLog('✗ Aucun élément vidéo', 'error');
    }

    if (canvas) {
      addLog('✓ Canvas présent', 'success');
    } else {
      addLog('✗ Aucun canvas', 'error');
    }

    addLog(`Scanning: ${scanning ? 'ACTIF' : 'INACTIF'}`, 'info');
    addLog(`Scanner ref: ${scannerRef.current ? 'PRÉSENT' : 'ABSENT'}`, 'info');
  };

  // Redémarrer le scanner
  const restartScanner = () => {
    addLog('Redémarrage du scanner...', 'process');
    setScanning(false);
    setIsProcessing(false);

    setTimeout(() => {
      setScanning(true);
      addLog('Scanner redémarré', 'success');
    }, 1000);
  };

  // Capture d'écran pour debug
  const captureDebugInfo = () => {
    const debugInfo = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      video: document.querySelector('#scanner-container video')?.outerHTML.substring(0, 200),
      scanning,
      hasPermission,
      scannerExists: !!scannerRef.current
    };

    console.log('=== DEBUG INFO ===', debugInfo);
    alert('Infos de debug envoyées à la console (F12)');
  };

  if (hasPermission === null) {
    return (
      <div className="qr-scanner-container minimal">
        <div className="scanner-loading">
          <div className="native-spinner"></div>
          <p>Démarrage caméra...</p>
        </div>
      </div>
    );
  }

  if (hasPermission === false) {
    return (
      <div className="qr-scanner-container minimal">
        <div className="permission-denied">
          <h4>Accès caméra requis</h4>
          <p>{error}</p>
          <button className="minimal-btn" onClick={() => window.location.reload()}>Réessayer</button>
          <button className="minimal-btn" onClick={onHide}>Fermer</button>
        </div>
      </div>
    );
  }

  return (
    <div className="qr-scanner-container minimal">
      {/* Floating Close Button */}
      <button className="floating-close-btn" onClick={onHide} aria-label="Fermer">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
      </button>

      {/* Zone de scan immersive */}
      <div className="scanner-area-full">
        {scanning && !isProcessing ? (
          <>
            <div id="scanner-container" className="scanner-container-full"></div>

            <div className="immersive-overlay">
              <div className="scan-target">
                <div className="corner-bracket tl"></div>
                <div className="corner-bracket tr"></div>
                <div className="corner-bracket bl"></div>
                <div className="corner-bracket br"></div>
                <div className="scan-anim-line"></div>
              </div>
              <p className="scan-label">Centrez le QR Code</p>
            </div>
          </>
        ) : (
          <div className="scanner-paused-fullscreen">
            {isProcessing ? (
              <div className="processing-state">
                <div className="native-spinner white"></div>
                <p>Validation en cours...</p>
              </div>
            ) : (
              <p>Scanner en pause</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientQRScanner;